import { css, html } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { Style } from "../Styles";
import { BaseElement } from "../BaseElement";
import { IPlace } from "../../models/Models";
import routie from "routie";

import "../map/PlacesMap";
import "./PlaceView";

import SlDialog from "@shoelace-style/shoelace/dist/components/dialog/dialog";
import { PlaceView, PlaceViewMode } from "./PlaceView";
import { getClosestPlaces, ILocation } from "../../models/ILocation";
import { watch } from "../internal/watch";
import { PlaceTable } from "../../backend/cloud/PlaceTable";
import { LocationType } from "../../../API";

@customElement("ds-place-view-dialog")
export class PlaceViewDialog extends BaseElement {
    static override styles = [
        Style,
        css`
            ds-place-view {
                width: 100%;
                height: 100%;
            }

            .full-screen-dialog::part(panel) {
                background-color: transparent;
            }
        `,
    ];
    @property({ type: Object }) place: IPlace | undefined;
    @property({ type: String }) viewMode: PlaceViewMode = "normal";
    @property({ type: Boolean }) noOpenInAppButton = false;
    @state() protected _closestPlaces: IPlace[] = [];

    @query("#dialog") protected _dialog: SlDialog | undefined;
    @query("#place-view") protected _placeView: PlaceView | undefined;

    @watch("place")
    async onPlaceChanged(oldValue: any, newValue: any) {
        if (oldValue != newValue && newValue) {
            const places = this._kernel.dataStore.getSource<PlaceTable>("places");

            // WARNING: This code is duplicated from cavedb-website/scripts/page-gen/generate-country-pages.js
            // and these have to be kept in sync by hand.

            // make sure we include all interesting places ahead of less interesting places
            const caves = getClosestPlaces(await places.all(), newValue.data.location, 8, 5000, [
                LocationType.Unknown,
                LocationType.NotFound,
                LocationType.Destroyed,
                LocationType.Depression,
                LocationType.SolutionTube,
                LocationType.RockyOutcrop,
                LocationType.Landmark,
            ]);

            const other = getClosestPlaces(await places.all(), newValue.data.location, 8, 2000, [
                LocationType.Cave,
                LocationType.Sinkhole,
                LocationType.Mine,
                LocationType.Destroyed,
                LocationType.NotFound,
                LocationType.Wreck,
                LocationType.Reef,
                LocationType.Jetty,
                LocationType.Muck,
                LocationType.Sand,
                LocationType.OceanWall,
                LocationType.Campsite,
            ]);

            this._closestPlaces = [...caves, ...other]
                .map((x) => x.place)
                .filter((x) => x.id !== newValue.id);
        }
    }

    public hide() {
        this._dialog?.hide();
        routie("/");
    }

    public show() {
        this._dialog?.show();
    }

    public setPlaceLocation(location: ILocation) {
        this._placeView?.setPlaceLocation(location);
    }

    override render() {
        return html` <sl-dialog
            open
            no-header
            id="dialog"
            class="full-screen-dialog"
            @sl-after-hide=${(e: Event) => {
                if (e.eventPhase !== e.AT_TARGET) {
                    e.stopPropagation();
                }
            }}
        >
            <ds-place-view
                id="place-view"
                .viewMode=${this.viewMode}
                .place=${this.place}
                .closestPlaces=${this._closestPlaces}
                @ds-close=${() => this.hide()}
                ?noOpenInAppButton=${this.noOpenInAppButton}
            ></ds-place-view>
        </sl-dialog>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-place-view-dialog": PlaceViewDialog;
    }
}
